import { Input, Select } from '@components'
import React, { useState } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { WaitNodeConfig } from '../nodes/types'
import { useWorkflowConfigForm } from './useWorkflowConfigForm'
import { optionIncludesValue } from './formOptionUtils'

interface Props {
  nodeId: string
  config: WaitNodeConfig
  onChange: (config: WaitNodeConfig) => void
}

const WAIT_UNIT_OPTIONS = [{ value: 'minutes' }, { value: 'hours' }, { value: 'days' }] as const

const isValidAmount = (rawValue: string): boolean => {
  if (!rawValue.trim()) return false
  const parsed = Number(rawValue)
  return Number.isInteger(parsed) && parsed >= 1
}

export const WaitConfigForm: React.FC<Props> = ({ nodeId, config, onChange }) => {
  const [t] = useTranslation()
  const { control, pushConfig } = useWorkflowConfigForm<WaitNodeConfig>({
    defaultValues: config,
    onChange
  })
  const amount = useWatch({ control, name: 'amount' })
  const unitCount = typeof amount === 'number' && amount >= 1 ? amount : 1
  const [amountDraft, setAmountDraft] = useState(
    typeof config.amount === 'number' ? String(config.amount) : ''
  )

  return (
    <>
      <Controller
        name="amount"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            id={`workflow-wait-amount-${nodeId}`}
            name={`workflow-wait-amount-${nodeId}`}
            type="number"
            min={1}
            step={1}
            label={t('workflows.config.wait.amount')}
            value={amountDraft}
            errors={fieldState.error}
            onChange={event => {
              setAmountDraft(event.target.value)

              if (isValidAmount(event.target.value)) {
                field.onChange(Number(event.target.value))
                pushConfig()
              }
            }}
            onBlur={event => {
              if (!isValidAmount(event.target.value)) {
                setAmountDraft(typeof field.value === 'number' ? String(field.value) : '')
              }

              field.onBlur()
            }}
          />
        )}
      />
      <Controller
        name="unit"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            id={`workflow-wait-unit-${nodeId}`}
            name={`workflow-wait-unit-${nodeId}`}
            label={t('workflows.config.wait.unit')}
            value={field.value}
            errors={fieldState.error}
            onChange={event => {
              field.onChange(event.target.value)
              pushConfig()
            }}
            onBlur={field.onBlur}
          >
            <option value="minutes">
              {t('workflows.config.wait.units.minutes', { count: unitCount })}
            </option>
            <option value="hours">
              {t('workflows.config.wait.units.hours', { count: unitCount })}
            </option>
            <option value="days">
              {t('workflows.config.wait.units.days', { count: unitCount })}
            </option>
            {!optionIncludesValue(WAIT_UNIT_OPTIONS, field.value) && field.value && (
              <option value={field.value}>{field.value}</option>
            )}
          </Select>
        )}
      />
    </>
  )
}
